/** Normalized public candidate profile (v1 camelCase). */
import { normalizeSkillsList } from './userProfile';

export type PublicProfileUser = {
  id: number;
  user_id?: number;
  email?: string;
  firstName?: string;
  lastName?: string;
  profileImg?: string;
  phoneNumber?: string;
  phone?: string;
  willingToRelocate?: string;
  workArrangements?: string;
  jobTitle?: string;
  gender?: string;
  dateOfBirth?: string;
  company?: string;
  flexibleType?: string;
  timeToStart?: string;
  salary?: string;
  linkedin?: string;
  username?: string;
  [key: string]: unknown;
};

const toNumberId = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const pickString = (
  raw: Record<string, unknown>,
  extended: Record<string, unknown>,
  key: string
) => {
  const val = raw[key] ?? extended[key];
  if (val == null || val === '') return undefined;
  return String(val);
};

/** Map v1 public profile API fields to camelCase the public profile UI expects. */
export const normalizePublicProfile = (
  raw: Record<string, unknown> | null | undefined
): PublicProfileUser | null | undefined => {
  if (!raw) return raw;

  const willingRaw = raw.willingToRelocate ?? raw.willing_to_relocate;
  let willingToRelocate = '';
  if (willingRaw === true || willingRaw === 'true') willingToRelocate = 'Yes';
  else if (willingRaw === false || willingRaw === 'false') willingToRelocate = 'No';
  else if (willingRaw != null && willingRaw !== '') willingToRelocate = String(willingRaw);

  const workRaw = String(raw.workArrangements ?? raw.work_arrangements ?? '').trim();
  const workArrangements = workRaw ? workRaw.charAt(0).toUpperCase() + workRaw.slice(1) : '';

  const phoneRaw = String(raw.phone ?? raw.phoneNumber ?? '').trim();
  const phoneNumber = phoneRaw.startsWith('+') ? phoneRaw.slice(1) : phoneRaw;

  const extended =
    raw.extended_info && typeof raw.extended_info === 'object'
      ? (raw.extended_info as Record<string, unknown>)
      : {};

  const id = toNumberId(raw.id ?? raw.user_id);

  return {
    ...raw,
    ...extended,
    id,
    user_id: id,
    firstName: String(raw.firstName ?? raw.first_name ?? ''),
    lastName: String(raw.lastName ?? raw.last_name ?? ''),
    profileImg: String(raw.profileImg ?? raw.profile_image ?? ''),
    phoneNumber,
    phone: phoneRaw,
    willingToRelocate,
    workArrangements,
    jobTitle: String(raw.jobTitle ?? raw.role_title ?? ''),
    gender: pickString(raw, extended, 'gender'),
    dateOfBirth: pickString(raw, extended, 'dateOfBirth'),
    company: pickString(raw, extended, 'company'),
    flexibleType: pickString(raw, extended, 'flexibleType'),
    timeToStart: pickString(raw, extended, 'timeToStart'),
    salary: String(raw.salary ?? raw.salary_expectation ?? ''),
    linkedin: String(raw.linkedin ?? ''),
    otherSocial: String(raw.otherSocial ?? raw.other_social ?? ''),
    skills: normalizeSkillsList(raw.skills ?? extended.skills),
    email: String(raw.email ?? ''),
    username: String(raw.username ?? ''),
  };
};
